import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrophy, 
  faHandshake, 
  faUserShield, 
  faLightbulb 
} from '@fortawesome/free-solid-svg-icons';
import './About.scss';

/**
 * About component for MET Connect website
 * 
 * Provides information about the company, mission, values, 
 * and why customers should choose MET Connect®
 * 
 * @returns {JSX.Element} The About component
 */
const About = () => {
  /**
   * Company values data
   */
  const companyValues = [
    {
      icon: faTrophy,
      title: 'Excellence',
      description: 'We strive for excellence in every solution we deliver, ensuring the highest quality and reliability.'
    },
    {
      icon: faHandshake,
      title: 'Partnership',
      description: 'We build lasting relationships with our clients, working together to solve complex challenges.'
    },
    {
      icon: faUserShield,
      title: 'Security',
      description: 'We prioritize security in everything we do, protecting your critical infrastructure and data.'
    },
    {
      icon: faLightbulb,
      title: 'Innovation',
      description: 'We continuously innovate to provide cutting-edge solutions that address evolving industry needs.'
    }
  ];

  return (
    <section className="about-section" id="about">
      <Container>
        <Row className="about-header">
          <Col lg={8} className="mx-auto text-center">
            <h2 className="section-title">About MET Connect<sup>®</sup></h2>
            <p className="section-subtitle">
              Leading the way in secure remote access and monitoring solutions for industrial control systems.
            </p>
          </Col>
        </Row>

        <Row className="about-content align-items-center">
          <Col lg={10} className="mx-auto">
            <div className="about-text">
              <h3>Who We Are</h3>
              <p>
                MET Connect® is a specialized provider of secure remote access and monitoring 
                solutions for control system networks. With years of experience in industrial 
                automation and cybersecurity, we understand the unique challenges facing 
                industrial operations today.
              </p>
              
              <h3>Our Mission</h3>
              <p>
                Our mission is to provide secure, reliable, and user-friendly remote access 
                and monitoring solutions that help businesses improve efficiency, reduce costs, 
                and enhance security. We're committed to delivering exceptional service and 
                support to ensure our clients' success.
              </p>
              
              <h3>Why Choose MET Connect<sup>®</sup></h3>
              <ul className="about-list">
                <li>Specialized expertise in industrial control systems</li>
                <li>Enterprise-grade security with industry-leading protocols</li>
                <li>Simplified connectivity without complex network configurations</li>
                <li>Comprehensive monitoring capabilities with real-time alerts</li>
                <li>Dedicated support from experienced professionals</li>
              </ul>
            </div>
          </Col>
        </Row>

        <Row className="values-section mt-5">
          <Col lg={12} className="text-center mb-4">
            <h3 className="values-title">Our Core Values</h3>
          </Col>
          
          {companyValues.map((value, index) => (
            <Col md={6} lg={3} key={index} className="mb-4">
              <Card className="value-card h-100">
                <Card.Body className="text-center">
                  <div className="value-icon">
                    <FontAwesomeIcon icon={value.icon} />
                  </div>
                  <h4 className="value-title">{value.title}</h4>
                  <p className="value-description">{value.description}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default About;